import React, { useState } from 'react';
import { Button } from '../../../../src/components/ui/button';
import { Badge } from '../../../../src/components/ui/badge';
import { BookOpen, HelpCircle, ExternalLink } from 'lucide-react';
import { GovernanceGlossaryModal } from './GovernanceGlossaryModal';

interface GlossaryTriggerProps {
  variant?: 'button' | 'badge' | 'help' | 'page-link';
  size?: 'sm' | 'lg' | 'default';
  className?: string;
  initialTerm?: string;
  showModal?: boolean; // If false, navigates to dedicated page instead
}

export function GlossaryTrigger({
  variant = 'button',
  size = 'default',
  className = '',
  initialTerm,
  showModal = true,
}: GlossaryTriggerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    if (showModal) {
      setIsModalOpen(true);
    } else {
      // Navigate to dedicated glossary page
      const baseUrl = '/governance/glossary';
      const url = initialTerm ? `${baseUrl}#${initialTerm}` : baseUrl;
      window.location.href = url;
    }
  };

  if (variant === 'page-link') {
    return (
      <Button variant="outline" size={size} onClick={handleOpenModal} className={`${className}`}>
        <ExternalLink className="w-4 h-4 mr-2" />
        Full Glossary Page
      </Button>
    );
  }

  if (variant === 'badge') {
    return (
      <>
        <Badge
          variant="outline"
          className={`cursor-pointer hover:bg-blue-50 hover:border-blue-200 transition-colors ${className}`}
          onClick={handleOpenModal}
        >
          <HelpCircle className="w-3 h-3 mr-1" />
          Glossary
          {!showModal && <ExternalLink className="w-3 h-3 ml-1" />}
        </Badge>
        {showModal && (
          <GovernanceGlossaryModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            initialTerm={initialTerm}
          />
        )}
      </>
    );
  }

  if (variant === 'help') {
    return (
      <>
        <button
          onClick={handleOpenModal}
          className={`inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm underline-offset-2 hover:underline ${className}`}
        >
          <HelpCircle className="w-4 h-4" />
          What do these terms mean?
          {!showModal && <ExternalLink className="w-3 h-3" />}
        </button>
        {showModal && (
          <GovernanceGlossaryModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            initialTerm={initialTerm}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Button variant="outline" size={size} onClick={handleOpenModal} className={`${className}`}>
        <BookOpen className="w-4 h-4 mr-2" />
        Governance Glossary
        {!showModal && <ExternalLink className="w-4 h-4 ml-1" />}
      </Button>
      {showModal && (
        <GovernanceGlossaryModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          initialTerm={initialTerm}
        />
      )}
    </>
  );
}
